<script setup>
import { useHomeStore } from "@/stores/HomeStore";

const features = [
  {
    name: "Real-time Tracking",
    description:
      "Monitor your portfolio's performance in real-time across multiple exchanges with live price updates and instant P&L calculations.",
    icon: "i-heroicons-bolt",
    gradient: "from-primary to-purple-600",
  },
  {
    name: "Advanced Analytics",
    description:
      "Deep dive into your trading performance with comprehensive charts, trend analysis, and detailed reports to optimize your strategy.",
    icon: "i-heroicons-chart-bar-square",
    gradient: "from-primary to-secondary",
  },
  {
    name: "Multi-Exchange Support",
    description:
      "Seamlessly connect and manage trades from Binance, Coinbase, Kraken, Firi, and more—all in one unified dashboard.",
    icon: "i-heroicons-squares-plus",
    gradient: "from-secondary to-blue-600",
  },
  {
    name: "Smart Alerts",
    description:
      "Set intelligent price alerts and get instant notifications when your trades hit profit targets or need attention.",
    icon: "i-heroicons-bell-alert",
    gradient: "from-primary/80 to-secondary/80",
  },
  {
    name: "Tax Calculations",
    description:
      "Automatically calculate your crypto taxes with support for Norwegian tax regulations and export-ready reports.",
    icon: "i-heroicons-calculator",
    gradient: "from-secondary/80 to-blue-700",
  },
  {
    name: "Portfolio Insights",
    description:
      "Get AI-powered insights and recommendations to improve your trading performance and maximize returns.",
    icon: "i-heroicons-light-bulb",
    gradient: "from-primary to-purple-700",
  },
];

const homeStore = useHomeStore();

const handleStartTrial = () => {
  homeStore.signIn = false;
};

const scrollToPricing = () => {
  const element = document.querySelector('#pricing');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};
</script>

<template>
  <div class="relative py-24 overflow-hidden bg-background sm:py-32" id="features">
    <!-- Background Decoration -->
    <div class="absolute inset-0 -z-10">
      <div
        class="absolute left-1/2 top-0 -translate-x-1/2 w-[90rem] h-[90rem] bg-gradient-to-tr from-primary/5 to-secondary/5 rounded-full blur-3xl"
        aria-hidden="true" />
    </div>

    <div class="px-6 mx-auto max-w-7xl lg:px-8">
      <!-- Section Header -->
      <div class="max-w-3xl mx-auto text-center">
        <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-primary/10 text-primary">
          Features
        </div>
        <h2 class="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Everything you need to
          <span class="block mt-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            trade with confidence
          </span>
        </h2>
        <p class="max-w-2xl mx-auto mt-6 text-lg leading-8 text-muted-foreground">
          Professional-grade tools designed for serious crypto traders. Track, analyze, and optimize your portfolio with ease.
        </p>
      </div>

      <!-- Features Grid with staggered animations -->
      <div class="grid max-w-6xl grid-cols-1 gap-8 mx-auto mt-20 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="(feature, index) in features"
          :key="feature.name"
          class="relative group"
          :style="{ animationDelay: `${index * 0.1}s` }">
          <div
            class="relative h-full p-8 transition-all duration-300 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-2xl hover:-translate-y-2 cursor-pointer">
            <!-- Icon with Gradient Background and pulse effect -->
            <div
              class="inline-flex items-center justify-center w-12 h-12 mb-6 rounded-xl relative"
              :class="`bg-gradient-to-br ${feature.gradient}`">
              <UIcon
                :name="feature.icon"
                class="w-6 h-6 text-white transition-transform duration-300 group-hover:scale-110" />
              <!-- Glow effect on hover -->
              <div 
                class="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-50 transition-opacity duration-300 blur-md"
                :class="`bg-gradient-to-br ${feature.gradient}`"></div>
            </div>

            <!-- Feature Content -->
            <h3 class="mb-3 text-xl font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
              {{ feature.name }}
            </h3>
            <p class="text-base leading-7 text-muted-foreground">
              {{ feature.description }}
            </p>

            <!-- Hover Effect Arrow with smooth transition -->
            <div
              class="absolute transition-all duration-300 opacity-0 bottom-8 right-8 group-hover:opacity-100 group-hover:translate-x-1">
              <div class="flex items-center gap-2 text-primary">
                <span class="text-sm font-medium">Learn more</span>
                <UIcon
                  name="i-heroicons-arrow-right"
                  class="w-5 h-5" />
              </div>
            </div>

            <!-- Subtle border glow on hover -->
            <div class="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style="box-shadow: 0 0 0 1px var(--primary), 0 0 20px -5px var(--primary);"></div>
          </div>
        </div>
      </div>

      <!-- CTA Section with enhanced interactions -->
      <div class="max-w-4xl p-8 mx-auto mt-20 text-center rounded-2xl sm:p-12 bg-gradient-to-r from-primary/10 via-purple-600/10 to-secondary/10 border border-border hover:border-primary/30 transition-all duration-300 group">
        <h3 class="text-2xl font-bold text-foreground sm:text-3xl group-hover:scale-105 transition-transform duration-200">
          Ready to take control of your crypto trading?
        </h3>
        <p class="max-w-2xl mx-auto mt-4 text-lg text-muted-foreground">
          Join thousands of traders who trust VimiCrypto to manage their portfolios.
        </p>
        <div class="flex flex-col items-center justify-center gap-4 mt-8 sm:flex-row">
          <button
            @click="handleStartTrial"
            class="px-8 py-4 text-base font-semibold transition-all duration-200 rounded-lg shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <span class="flex items-center gap-2">
              Start Free Trial
              <UIcon name="i-heroicons-arrow-right" class="w-4 h-4" />
            </span>
          </button>
          <button
            @click="scrollToPricing"
            class="px-8 py-4 text-base font-semibold transition-all duration-200 rounded-lg bg-card text-foreground border border-border hover:bg-accent hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            View Pricing
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
